import { useMemo, useState } from 'react';
// import { PlusCircle } from 'react-feather';

import { Link, NotFound, Pagination, TeamSelector, Typography, Wrapper } from '@/components';
import { useAuthUser, useAuthToken, useTeamGet, useTeamSelect } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { useCampaigns } from '@/hooks/client';
// import { TranslatedLink } from '@/utils/translatedComponents';
import { getPlannerUrlWithJwt } from '@/utils/links';

import styles from './Campaigns.module.css';
import { Campaign } from './components/Campaign';

const COLUMNS = ['Brand', 'Name', 'Budget', 'Date', 'Status'];

export function Campaigns() {
  const [currentPage, setCurrentPage] = useState(0);
  const { t } = useTranslation();
  const user = useAuthUser();
  const jwtToken = useAuthToken();

  const plannerUrl = getPlannerUrlWithJwt(jwtToken);

  const { data, isLoading } = useCampaigns({ currentPage });
  const {
    data: { teams },
    isLoading: isLoadingTeams
  } = useTeamGet(user?.team?.team_id);

  const memoData = useMemo(() => data || {}, [data]);
  const memoTeams = useMemo(() => (teams as Api.Team[]) || [], [teams]);

  const { isLoading: isSwitchingTeam, mutate: teamSwitch } = useTeamSelect({
    teamId: user?.team?.team_id
  });

  if (!user?.is_client) return null;

  const handleTeamSwitch = (selectedTeamId: string) => {
    teamSwitch(selectedTeamId);
  };

  function prevPage() {
    return setCurrentPage((prevState) => prevState - 1);
  }

  function nextPage() {
    return setCurrentPage((prevState) => prevState + 1);
  }

  const maxPages = Math.ceil(memoData.countTotal / memoData.pageSize) || 0;

  return (
    <Wrapper
      isLoading={isLoading}
      heading={
        <div className={styles.campaignsHeading}>
          <div className={styles.campaignsHeadingTeam}>
            <Typography sizes={['h2']}>
              <h2>{t('campaigns.heading')}</h2>
            </Typography>

            {memoTeams.length > 1 && (
              <div>
                <TeamSelector
                  teams={memoTeams}
                  currentTeamId={user?.team?.team_id}
                  currentTeamName={user?.team?.name}
                  onChange={handleTeamSwitch}
                  disabled={isLoadingTeams}
                  isLoading={isSwitchingTeam}
                />
              </div>
            )}
          </div>

          <div className={styles.campaignsHeadingColumn}>
            <Link href={plannerUrl} theme="cta" target="_blank">
              {t('campaigns.cta')}
            </Link>
            {/* <TranslatedLink href={isProduction ? '/connect/campaigns/new' : '/create-campaign'}>
              <div className={styles.campaignsHeadingCta}>
                <span className={styles.campaignsHeadingCtaText}>
                  <Typography sizes={['label-secondary']}>{t('campaigns.cta')}</Typography>
                </span>

                <PlusCircle />
              </div>
            </TranslatedLink> */}
          </div>
        </div>
      }
    >
      {memoData.campaigns?.length ? (
        <>
          {maxPages > 1 && (
            <div className={styles.campaignPagination}>
              <Pagination
                currentPage={currentPage}
                handleNextPage={nextPage}
                handlePrevPage={prevPage}
                maxPages={maxPages}
                hasMore={memoData.hasMore}
              />
            </div>
          )}

          <div className={styles.campaignsList}>
            <div className={styles.campaignListColumns}>
              {COLUMNS.map((column) => (
                <span className={styles.campaignListColumnsItem} key={`column-${column}`}>
                  <Typography sizes={['label']}>{t(`campaigns.column${column}`)}</Typography>
                </span>
              ))}
            </div>

            {memoData.campaigns?.map((campaign: TypeAny) => (
              <Campaign key={`campaign-${campaign.campaign_id}`} campaign={campaign} />
            ))}
          </div>

          {maxPages > 1 && (
            <div className={styles.campaignPagination}>
              <Pagination
                currentPage={currentPage}
                handleNextPage={nextPage}
                handlePrevPage={prevPage}
                maxPages={maxPages}
                hasMore={memoData.hasMore}
              />
            </div>
          )}
        </>
      ) : (
        <div className={styles.campaignsNotFound}>
          <NotFound text={t('campaigns.notFound')} />
        </div>
      )}
    </Wrapper>
  );
}
